import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FootballBoard } from '../components/FootballBoard';
import { Controls } from '../components/Controls';
import { toast } from 'sonner';

// Formación 4-4-2
// Equipo Blanco (izquierda)
const WHITE_TEAM = [
  // Portero
  { id: 1, number: 1, x: 80, y: 544, color: '#ffffff', isGoalkeeper: true, isBlaugrana: false },
  // Defensas (4)
  { id: 2, number: 2, x: 250, y: 200, color: '#ffffff', isGoalkeeper: false, isBlaugrana: false },
  { id: 3, number: 3, x: 250, y: 400, color: '#ffffff', isGoalkeeper: false, isBlaugrana: false },
  { id: 4, number: 4, x: 250, y: 688, color: '#ffffff', isGoalkeeper: false, isBlaugrana: false },
  { id: 5, number: 5, x: 250, y: 888, color: '#ffffff', isGoalkeeper: false, isBlaugrana: false },
  // Mediocampistas (4)
  { id: 6, number: 6, x: 480, y: 200, color: '#ffffff', isGoalkeeper: false, isBlaugrana: false },
  { id: 7, number: 7, x: 480, y: 400, color: '#ffffff', isGoalkeeper: false, isBlaugrana: false },
  { id: 8, number: 8, x: 480, y: 688, color: '#ffffff', isGoalkeeper: false, isBlaugrana: false },
  { id: 9, number: 9, x: 480, y: 888, color: '#ffffff', isGoalkeeper: false, isBlaugrana: false },
  // Delanteros (2)
  { id: 10, number: 10, x: 710, y: 400, color: '#ffffff', isGoalkeeper: false, isBlaugrana: false },
  { id: 11, number: 11, x: 710, y: 688, color: '#ffffff', isGoalkeeper: false, isBlaugrana: false },
];

// Equipo Azulgrana (derecha) - todos con rayas azul y grana (Barcelona)
const BLAUGRANA_TEAM = [
  // Portero - verde
  { id: 12, number: 1, x: 1600, y: 544, color: '#00A859', isGoalkeeper: true, isBlaugrana: false },
  // Defensas (4) - todos azulgrana
  { id: 13, number: 2, x: 1430, y: 200, color: '#004C99', isGoalkeeper: false, isBlaugrana: true },
  { id: 14, number: 3, x: 1430, y: 400, color: '#004C99', isGoalkeeper: false, isBlaugrana: true },
  { id: 15, number: 4, x: 1430, y: 688, color: '#004C99', isGoalkeeper: false, isBlaugrana: true },
  { id: 16, number: 5, x: 1430, y: 888, color: '#004C99', isGoalkeeper: false, isBlaugrana: true },
  // Mediocampistas (4) - todos azulgrana
  { id: 17, number: 6, x: 1200, y: 200, color: '#004C99', isGoalkeeper: false, isBlaugrana: true },
  { id: 18, number: 7, x: 1200, y: 400, color: '#004C99', isGoalkeeper: false, isBlaugrana: true },
  { id: 19, number: 8, x: 1200, y: 688, color: '#004C99', isGoalkeeper: false, isBlaugrana: true },
  { id: 20, number: 9, x: 1200, y: 888, color: '#004C99', isGoalkeeper: false, isBlaugrana: true },
  // Delanteros (2) - todos azulgrana
  { id: 21, number: 10, x: 970, y: 400, color: '#004C99', isGoalkeeper: false, isBlaugrana: true },
  { id: 22, number: 11, x: 970, y: 688, color: '#004C99', isGoalkeeper: false, isBlaugrana: true },
];

const INITIAL_PLAYERS = [...WHITE_TEAM, ...BLAUGRANA_TEAM];
const INITIAL_BALL = { x: 840, y: 544 }; // Centro del campo

export const FootballTacticalBoard = () => {
  const { t } = useTranslation();
  const [players, setPlayers] = useState(INITIAL_PLAYERS);
  const [ball, setBall] = useState(INITIAL_BALL);
  const [drawings, setDrawings] = useState([]);
  const [currentTool, setCurrentTool] = useState(null);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('toolchange', { detail: { tool: currentTool } }));
  }, [currentTool]);

  const handleDragPlayer = (index, x, y) => {
    setPlayers((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], x, y };
      return updated;
    });
  };

  const handleDragBall = (x, y) => {
    setBall({ x, y });
  };

  const handleDrawLine = () => {
    if (currentTool === 'line') {
      setCurrentTool(null);
      toast.info('Drag mode active');
    } else {
      setCurrentTool('line');
      toast.info('Click to draw line - start and end points');
    }
  };

  const handleDrawCircle = () => {
    if (currentTool === 'circle') {
      setCurrentTool(null);
      toast.info('Drag mode active');
    } else {
      setCurrentTool('circle');
      toast.info('Click to draw circle - center and radius');
    }
  };

  const handleFreeDraw = () => {
    if (currentTool === 'freedraw') {
      setCurrentTool(null);
      toast.info('Drag mode active');
    } else {
      setCurrentTool('freedraw');
      toast.info('Free draw mode - drag to draw');
    }
  };

  const handleDrawingComplete = (drawing) => {
    setDrawings((prev) => [...prev, drawing]);
  };

  const handleUndo = () => {
    if (drawings.length > 0) {
      setDrawings((prev) => prev.slice(0, -1));
      toast.success('Undo');
    }
  };

  const handleReset = () => {
    setPlayers(INITIAL_PLAYERS);
    setBall(INITIAL_BALL);
    setDrawings([]);
    setCurrentTool(null);
    toast.success('Reset');
  };

  return (
    <div className="flex flex-col h-screen w-full bg-background">
      {/* Controls */}
      <Controls
        onDrawLine={handleDrawLine}
        onDrawCircle={handleDrawCircle}
        onFreeDraw={handleFreeDraw}
        onUndo={handleUndo}
        onReset={handleReset}
        currentTool={currentTool}
      />

      {/* Board */}
      <div className="flex-1 overflow-hidden relative">
        <FootballBoard
          players={players}
          ball={ball}
          onDragPlayer={handleDragPlayer}
          onDragBall={handleDragBall}
          drawings={drawings}
          onDrawingComplete={handleDrawingComplete}
        />
        
        {/* Branding watermark */}
        <div className="absolute bottom-2 right-2 text-xs text-white/60 italic pointer-events-none">
          {t('branding')}
        </div>
      </div>
    </div>
  );
};